/* eslint-disable max-len */
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Typography } from 'src/components/common/Typography';
import { EnumRoutePaths } from 'src/configs/routes';
import { makePath } from 'src/helpers/transformers';
import { allTagChecker, scrollToTop, setBodyOverflow } from 'src/helpers/utils';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { useBannerActions } from 'src/hooks/useBannerActions';
import { useGameAction } from 'src/hooks/useGameAction';
import { getBlogs } from 'src/services/cmsApi';
import { CmsPage } from 'src/types/cms/cmsCommonTypes';
import { EnumCmsPromotionPostTagsEnum, EnumCmsPromotionPostViewEnum } from 'src/types/cms/cmsEnums';
import { CmsPromotionPageFields, CmsPromotionPagesResponse } from 'src/types/cms/cmsPageTypes';
import { RootState } from 'src/types/store-types';
import BlogInfoPage from './BlogInfoPage/BlogInfoPage';
import CardWithInfoPage from './CardWithInfoPage/CardWithInfoPage';
import PromotionModal from './PromotionModal';
import './promotions.scss';

const StaticBlogPage: FC = () => {
  const { t }: Translation = useTranslation();
  const { locale } = useI18n();

  const sessionRecorder = useSessionRecorder();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { staticPathParam } = useParams();

  const { providerAction, loginAction, registrAction, promotionAction, categoryAction, externalUrlAction } =
    useBannerActions();

  const { handleActionGame } = useGameAction();

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { categoriesData } = useSelector((state: RootState) => state.configs);
  const { tags } = useSelector((state: RootState) => state.games);

  const [tagList, setTagList] = useState<any[]>([]);

  const [blogs, setBlogs] = useState<CmsPromotionPagesResponse['result'] | null>(null);
  const [blog, setBlog] = useState<CmsPage<CmsPromotionPageFields> | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [showPromoModal, setShowPromoModal] = useState<boolean>(false);

  const blogTags = useMemo(() => {
    if (!blogs) return null;

    const targetArray = blogs.reduce((acc: EnumCmsPromotionPostTagsEnum[], cur) => {
      if (cur?.content?.pageFields.tags?.[0]) {
        acc.push(...cur?.content?.pageFields.tags.map((tag) => tag));
      }
      return acc;
    }, [] as EnumCmsPromotionPostTagsEnum[]);

    return allTagChecker(Array.from(new Set(targetArray)));
  }, [blogs]);

  const filteredBlogsContent = useMemo(() => {
    const blogsWithContent = blogs?.filter((blog) => blog.content);

    const sortedBlogs = blogsWithContent?.sort((a, b) => {
      const aValue = a.content?.pageFields?.priority ? +a.content.pageFields.priority : 0;
      const bValue = b.content?.pageFields?.priority ? +b.content.pageFields.priority : 0;
      return aValue - bValue;
    });

    if (!blogTags || blogTags.length <= 0 || selectedTag === 'all') return sortedBlogs;

    return sortedBlogs?.filter((blog) => blog.content?.pageFields.tags?.some((m: string) => m === selectedTag));
  }, [blogs, selectedTag, blogTags]);

  const handleClick = (blogInfo: CmsPage<CmsPromotionPageFields>): void => {
    sessionRecorder?.trackEvent('Blog card click', {
      blogName: blogInfo.content?.pageFields.cardTitle,
    });

    const { externalContentUrl, viewType } = blogInfo.content?.pageFields ?? {};
    const slugPath = `${EnumRoutePaths.BLOG}/${makePath(blogInfo.slug)}`;

    if (externalContentUrl && viewType === EnumCmsPromotionPostViewEnum.NEW_PAGE) {
      window.open(externalContentUrl);
      return;
    }

    setBlog(blogInfo);
    navigate(slugPath);

    if (!externalContentUrl) {
      return;
    }

    if (viewType === EnumCmsPromotionPostViewEnum.DIALOG) {
      setShowPromoModal(true);
      setBodyOverflow('unset');
      return;
    }

    // INLINE or default
    setShowPromoModal(false);
    setBodyOverflow('set');
  };

  const closePromoModal = (): void => {
    setBodyOverflow('set');
    setShowPromoModal(false);
    navigate(EnumRoutePaths.BLOG);
    setBlog(null);
  };

  const handleBack = (): void => {
    navigate(EnumRoutePaths.BLOG);
    setBlog(null);
  };

  const handleSelectetTag = (tag: string): void => {
    setSelectedTag(tag);
  };

  const redirect = (): void => {
    const variant = blog?.content?.pageFields?.cta?.variant;
    const param = blog?.content?.pageFields?.cta?.param;

    switch (variant) {
      case 'goToRegister':
        registrAction();
        break;
      case 'goToLogin':
        loginAction();
        break;
      case 'goToPromotion':
      case 'goToPromotions':
        promotionAction(param);
        break;
      case 'goToProvider':
        providerAction({ provider: param, tags: tags, tagList: tagList });
        break;
      case 'goToExternalUrl':
        externalUrlAction(param);
        break;
      case 'goToGame':
        handleActionGame(param as number);
        break;
      case 'goToCategory':
        categoryAction(param);
        break;
      case 'goToRace':
      case 'noAction':
        break;
      default:
        return;
    }
  };

  const getBlogData = (): void => {
    getBlogs(locale).then((res) => setBlogs(res.result));
  };

  useEffect(() => {
    scrollToTop();
  }, [blog]);

  useEffect(() => {
    getBlogData();
  }, [locale]);

  useEffect(() => {
    categoriesData &&
      setTagList(categoriesData[selectedCategory]?.tags?.filter((f) => f.tagName === 'allGames')?.[0].tagGames || []);
  }, [categoriesData]);

  useEffect(() => {
    if (staticPathParam && !!blogs?.length) {
      const blog = blogs?.filter((blog) => makePath(blog.slug) === staticPathParam)[0];
      blog ? setBlog(blog) : navigate(pathname);
    }
  }, [staticPathParam, blog, new URLSearchParams(search)]);

  useEffect(() => {
    sessionRecorder?.trackEvent('Blogs page view');
  }, []);

  if (!filteredBlogsContent) return null;

  return (
    <div className="promotions__container">
      {!blog && (
        <div className="promotions__inner">
          <Typography className="promotions__title" variant={'h6'}>
            {t('blog')}
          </Typography>
          {blogTags && blogTags?.length > 0 && (
            <div className="promotions__tags">
              {blogTags.map((tag: string) => (
                <div
                  key={tag}
                  onClick={handleSelectetTag.bind(null, tag)}
                  className={`promotions__tag ${tag === selectedTag ? 'promotions__tag--active' : ''}`}
                >
                  {t(tag) ?? tag}
                </div>
              ))}
            </div>
          )}
          <div className="promotions__bonus-list">
            {filteredBlogsContent?.map((cardItem) => (
              <CardWithInfoPage key={cardItem.id} cardInfo={cardItem} onBtnClick={handleClick} />
            ))}
          </div>
        </div>
      )}
      {showPromoModal && (
        <PromotionModal externalUrl={blog?.content?.pageFields.externalContentUrl || ''} close={closePromoModal} />
      )}
      {blog && !showPromoModal && (
        <BlogInfoPage handleBack={handleBack} handleRedirect={redirect} blogPageInfo={blog} />
      )}
    </div>
  );
};
export default StaticBlogPage;
